import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  TextField,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Done as DoneIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSnackbar } from '../contexts/SnackbarContext.tsx';
import { cashDepositApi, CashDepositEntry } from '../services/cashDepositService.ts';
import { employeeApi, Employee } from '../services/employeeService.ts';

const WITHDRAWAL_SOURCES = ['Lottery', 'Cash Safe'] as const;

const emptyForm = {
  date: '',
  employeeId: '',
  cashDeposit: '',
  cashWithdrawals: '',
  withdrawalSource: '',
  total: '',
  notes: '',
};
type CashDepositForm = typeof emptyForm;

// Total is always derived as Cash Deposit - Cash Withdrawals.
const computeTotal = (snapshot: CashDepositForm): string =>
  ((parseFloat(snapshot.cashDeposit) || 0) - (parseFloat(snapshot.cashWithdrawals) || 0)).toFixed(2);

const CashDepositsPage: React.FC = () => {
  const { sessionToken, user } = useAuth();
  const isAdmin = user?.type === 'admin';
  const { showSuccess, showError } = useSnackbar();

  const [entries, setEntries] = useState<CashDepositEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);

  // Only Asst. Manager / Manager employees can be picked for the Name column.
  const managerEmployees = employees.filter((e) => (e.designation || '').toLowerCase().includes('manager'));

  // Blank add-row at the bottom of the table.
  const [addForm, setAddForm] = useState<CashDepositForm>(emptyForm);
  const [adding, setAdding] = useState(false);

  // Inline row edit - auto-saves ~900ms after the last change, same pattern as the
  // rest of this app's inline-editable tables.
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<CashDepositForm>(emptyForm);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'pending' | 'saving' | 'saved' | 'error'>('idle');
  const [autoSaveError, setAutoSaveError] = useState('');
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    };
  }, []);

  const headerCellSx = {
    color: '#fff',
    fontWeight: 700,
    fontSize: '0.82rem',
    letterSpacing: '0.05em',
    textTransform: 'uppercase' as const,
    border: '2px solid #7f0000',
    py: 1.5,
    px: 2,
  };

  const fetchEntries = async () => {
    if (!sessionToken) return;
    try {
      setLoading(true);
      const response = await cashDepositApi.getEntries({ page: 1, limit: 100 }, sessionToken);
      setEntries(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch cash deposits';
      showError(message);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    if (!sessionToken) return;
    try {
      setEmployeesLoading(true);
      const response = await employeeApi.getEmployees({ page: 1, limit: 500 }, sessionToken);
      setEmployees(response.data);
    } catch (err) {
      // Non-fatal: the Name dropdown just shows empty if this fails.
      console.error('Error fetching employees:', err);
    } finally {
      setEmployeesLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
    fetchEmployees();
    setAddForm({ ...emptyForm, date: new Date().toISOString().slice(0, 10) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionToken]);

  const employeeName = (id: number | null) => {
    if (!id) return '—';
    return employees.find((e) => e.id === id)?.name || `Employee ${id}`;
  };

  const handleDelete = async (id: number) => {
    if (!sessionToken || !isAdmin) return;
    if (!window.confirm('Delete this entry?')) return;
    try {
      await cashDepositApi.deleteEntry(id, sessionToken);
      setEntries(prev => prev.filter(e => e.id !== id));
      if (editingId === id) {
        if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
        setEditingId(null);
      }
      showSuccess('Entry deleted.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete entry';
      showError(message);
    }
  };

  // Validates a form snapshot and returns the parsed values, or null (with a
  // toast/status message) if invalid. Shared by the Add row and inline edit auto-save.
  const validateForm = (snapshot: CashDepositForm, onInvalid: (message: string) => void) => {
    if (!snapshot.date || !snapshot.employeeId || snapshot.cashDeposit === '' || snapshot.cashWithdrawals === '') {
      onInvalid('Please fill in Date, Name, Cash Deposit, and Cash Withdrawals.');
      return null;
    }
    const cashDeposit = parseFloat(snapshot.cashDeposit);
    if (isNaN(cashDeposit) || cashDeposit < 0) {
      onInvalid('Cash Deposit must be a valid number.');
      return null;
    }
    const cashWithdrawals = parseFloat(snapshot.cashWithdrawals);
    if (isNaN(cashWithdrawals) || cashWithdrawals < 0) {
      onInvalid('Cash Withdrawals must be a valid number.');
      return null;
    }
    return {
      date: snapshot.date,
      employeeId: parseInt(snapshot.employeeId, 10),
      cashDeposit,
      cashWithdrawals,
      withdrawalSource: (snapshot.withdrawalSource || undefined) as 'Lottery' | 'Cash Safe' | undefined,
      total: parseFloat(computeTotal(snapshot)),
      notes: snapshot.notes.trim() || undefined,
    };
  };

  // --- Add row ---

  const handleAddFormChange = (field: keyof CashDepositForm, value: string) => {
    setAddForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'cashDeposit' || field === 'cashWithdrawals') {
        next.total = computeTotal(next);
      }
      return next;
    });
  };

  const handleAdd = async () => {
    if (!sessionToken) return;
    const parsed = validateForm(addForm, showError);
    if (!parsed) return;

    setAdding(true);
    try {
      const created = await cashDepositApi.createEntry(parsed, sessionToken);
      setEntries(prev => [created, ...prev]);
      setAddForm({ ...emptyForm, date: new Date().toISOString().slice(0, 10) });
      showSuccess('Entry added.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add entry';
      showError(message);
    } finally {
      setAdding(false);
    }
  };

  // --- Inline edit ---

  const startEdit = (entry: CashDepositEntry) => {
    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    setEditingId(entry.id);
    setAutoSaveStatus('idle');
    setAutoSaveError('');
    setEditForm({
      date: entry.date.slice(0, 10),
      employeeId: entry.employeeId ? entry.employeeId.toString() : '',
      cashDeposit: entry.cashDeposit.toString(),
      cashWithdrawals: entry.cashWithdrawals.toString(),
      withdrawalSource: entry.withdrawalSource || '',
      total: entry.total.toString(),
      notes: entry.notes || '',
    });
  };

  const performAutoSave = useCallback(
    async (id: number, snapshot: CashDepositForm) => {
      if (!sessionToken) return;
      const parsed = validateForm(snapshot, (message) => {
        setAutoSaveStatus('error');
        setAutoSaveError(message);
      });
      if (!parsed) return;

      setAutoSaveStatus('saving');
      try {
        const updated = await cashDepositApi.updateEntry(id, parsed, sessionToken);
        setEntries(prev => prev.map(e => (e.id === id ? updated : e)));
        setAutoSaveStatus('saved');
      } catch (err) {
        setAutoSaveStatus('error');
        setAutoSaveError(err instanceof Error ? err.message : 'Failed to save changes');
      }
    },
    [sessionToken]
  );

  const handleEditFormChange = (field: keyof CashDepositForm, value: string) => {
    setEditForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'cashDeposit' || field === 'cashWithdrawals') {
        next.total = computeTotal(next);
      }
      if (editingId !== null) {
        if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
        setAutoSaveStatus('pending');
        autoSaveTimeoutRef.current = setTimeout(() => {
          autoSaveTimeoutRef.current = null;
          performAutoSave(editingId, next);
        }, 900);
      }
      return next;
    });
  };

  const finishEdit = async () => {
    if (autoSaveTimeoutRef.current && editingId !== null) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
      await performAutoSave(editingId, editForm);
    }
    setEditingId(null);
    setAutoSaveStatus('idle');
  };

  const formatDate = (d: string) =>
    d ? new Date(d.slice(0, 10) + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

  const totalAmount = entries.reduce((sum, e) => sum + (Number(e.total) || 0), 0);
  const columns = ['ID', 'Date', 'Name', 'Cash Deposit', 'Cash Withdrawals', 'Withdrawal Source', 'Total/Bank Deposit', 'Notes', 'Actions'];
  const columnAligns: Array<'left' | 'right' | 'center'> = ['left', 'left', 'left', 'right', 'right', 'left', 'right', 'left', 'center'];
  const columnWidths = ['5%', '10%', '14%', '10%', '11%', '11%', '11%', '18%', '10%'];

  const numberField = (
    value: string,
    onChange: (v: string) => void,
    disabled?: boolean,
    helperText?: string,
  ) => (
    <TextField
      size="small"
      type="number"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      helperText={helperText}
      inputProps={{ min: 0, step: 0.01 }}
      InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, fontSize: '0.85rem' }}>$</Typography> }}
      fullWidth
    />
  );

  const nameField = (value: string, onChange: (v: string) => void) => (
    <FormControl size="small" fullWidth disabled={employeesLoading}>
      <Select value={value} displayEmpty onChange={(e) => onChange(e.target.value as string)}>
        <MenuItem value=""><em>Select…</em></MenuItem>
        {managerEmployees.map((emp) => (
          <MenuItem key={emp.id} value={emp.id.toString()}>{emp.name}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const notesField = (value: string, onChange: (v: string) => void) => (
    <TextField
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Optional"
      fullWidth
    />
  );

  const withdrawalSourceField = (value: string, onChange: (v: string) => void) => (
    <FormControl size="small" fullWidth>
      <Select value={value} displayEmpty onChange={(e) => onChange(e.target.value as string)}>
        <MenuItem value=""><em>Select…</em></MenuItem>
        {WITHDRAWAL_SOURCES.map((src) => (
          <MenuItem key={src} value={src}>{src}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" sx={{ color: "#7f0000", fontWeight: 700, flex: "0 0 auto", whiteSpace: "nowrap" }}>
          Date: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </Typography>
        <Typography variant="h4" component="h1" sx={{ flex: 1, textAlign: 'center', fontWeight: 700, textTransform: 'uppercase' }}>
          Cash Deposits
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {entries.length} entr{entries.length !== 1 ? 'ies' : 'y'}
        {entries.length > 0 && ` · Total: $${totalAmount.toFixed(2)}`}
      </Typography>

      <Paper sx={{ width: '100%', overflow: 'hidden', border: '2px solid #9e9e9e' }}>
        <TableContainer>
          <Table sx={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#b71c1c' }}>
                {columns.map((h, i) => (
                  <TableCell
                    key={h}
                    align={columnAligns[i]}
                    sx={{ ...headerCellSx, width: columnWidths[i] }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 5, border: '2px solid #9e9e9e' }}>
                    <CircularProgress size={28} />
                    <Typography variant="body2" sx={{ mt: 1 }}>Loading cash deposits...</Typography>
                  </TableCell>
                </TableRow>
              ) : entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 5, border: '2px solid #9e9e9e', color: 'text.secondary' }}>
                    No entries yet. Use the row below to add one.
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((entry, idx) => {
                  const isEditing = editingId === entry.id;
                  return (
                    <TableRow
                      key={entry.id}
                      sx={{
                        backgroundColor: isEditing ? '#fce4e4' : idx % 2 === 0 ? '#fff' : '#fdf5f5',
                        '&:hover': { backgroundColor: '#fce4e4' },
                        '& td': { border: '2px solid #9e9e9e', py: 1.25, px: 2 },
                      }}
                    >
                      <TableCell sx={{ width: columnWidths[0] }}>
                        <Typography variant="body2" color="text.secondary">{entry.id}</Typography>
                      </TableCell>
                      <TableCell sx={{ width: columnWidths[1] }}>
                        {isEditing ? (
                          <TextField
                            size="small"
                            type="date"
                            value={editForm.date}
                            onChange={(e) => handleEditFormChange('date', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                          />
                        ) : (
                          formatDate(entry.date)
                        )}
                      </TableCell>
                      <TableCell sx={{ width: columnWidths[2] }}>
                        {isEditing ? nameField(editForm.employeeId, (v) => handleEditFormChange('employeeId', v)) : employeeName(entry.employeeId)}
                      </TableCell>
                      <TableCell align="right" sx={{ width: columnWidths[3] }}>
                        {isEditing ? numberField(editForm.cashDeposit, (v) => handleEditFormChange('cashDeposit', v)) : `$${(Number(entry.cashDeposit) || 0).toFixed(2)}`}
                      </TableCell>
                      <TableCell align="right" sx={{ width: columnWidths[4] }}>
                        {isEditing ? numberField(editForm.cashWithdrawals, (v) => handleEditFormChange('cashWithdrawals', v)) : `$${(Number(entry.cashWithdrawals) || 0).toFixed(2)}`}
                      </TableCell>
                      <TableCell sx={{ width: columnWidths[5] }}>
                        {isEditing ? withdrawalSourceField(editForm.withdrawalSource, (v) => handleEditFormChange('withdrawalSource', v)) : (entry.withdrawalSource || '—')}
                      </TableCell>
                      <TableCell align="right" sx={{ width: columnWidths[6] }}>
                        {isEditing ? (
                          numberField(editForm.total, () => {}, true, 'Auto-calculated')
                        ) : (
                          <Typography variant="body2" fontWeight="600">
                            ${(Number(entry.total) || 0).toFixed(2)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ width: columnWidths[7], maxWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {isEditing ? notesField(editForm.notes, (v) => handleEditFormChange('notes', v)) : (entry.notes || '—')}
                      </TableCell>
                      <TableCell align="center" sx={{ width: columnWidths[8] }}>
                        {isEditing ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                            <Tooltip title="Done editing">
                              <span>
                                <IconButton size="small" color="success" onClick={finishEdit} disabled={autoSaveStatus === 'saving'}>
                                  <DoneIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={autoSaveStatus === 'error' ? (autoSaveError || 'Failed to save changes') : ''}>
                              <Typography variant="caption" color={autoSaveStatus === 'error' ? 'error' : 'text.secondary'} sx={{ whiteSpace: 'nowrap' }}>
                                {autoSaveStatus === 'saving' && 'Saving…'}
                                {autoSaveStatus === 'pending' && 'Unsaved…'}
                                {autoSaveStatus === 'saved' && 'Saved'}
                                {autoSaveStatus === 'error' && 'Error'}
                              </Typography>
                            </Tooltip>
                          </Box>
                        ) : (
                          <>
                            <Tooltip title="Edit">
                              <IconButton size="small" color="primary" onClick={() => startEdit(entry)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {isAdmin && (
                              <Tooltip title="Delete">
                                <IconButton size="small" color="error" onClick={() => handleDelete(entry.id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}

              {/* Blank add-row */}
              <TableRow sx={{ '& td': { border: '2px solid #9e9e9e', py: 1, px: 2, backgroundColor: '#fafafa' } }}>
                <TableCell sx={{ width: columnWidths[0] }} />
                <TableCell sx={{ width: columnWidths[1] }}>
                  <TextField
                    size="small"
                    type="date"
                    value={addForm.date}
                    onChange={(e) => handleAddFormChange('date', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </TableCell>
                <TableCell sx={{ width: columnWidths[2] }}>{nameField(addForm.employeeId, (v) => handleAddFormChange('employeeId', v))}</TableCell>
                <TableCell align="right" sx={{ width: columnWidths[3] }}>{numberField(addForm.cashDeposit, (v) => handleAddFormChange('cashDeposit', v))}</TableCell>
                <TableCell align="right" sx={{ width: columnWidths[4] }}>{numberField(addForm.cashWithdrawals, (v) => handleAddFormChange('cashWithdrawals', v))}</TableCell>
                <TableCell sx={{ width: columnWidths[5] }}>{withdrawalSourceField(addForm.withdrawalSource, (v) => handleAddFormChange('withdrawalSource', v))}</TableCell>
                <TableCell align="right" sx={{ width: columnWidths[6] }}>{numberField(addForm.total, () => {}, true, 'Auto-calculated')}</TableCell>
                <TableCell sx={{ width: columnWidths[7] }}>{notesField(addForm.notes, (v) => handleAddFormChange('notes', v))}</TableCell>
                <TableCell align="center" sx={{ width: columnWidths[8] }}>
                  <Tooltip title="Add entry">
                    <span>
                      <IconButton size="small" color="success" onClick={handleAdd} disabled={adding}>
                        {adding ? <CircularProgress size={18} /> : <AddIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>

              {entries.length > 0 && (
                <TableRow sx={{ backgroundColor: '#b71c1c', '& td': { border: '2px solid #7f0000', py: 1.25, px: 2, color: '#fff', fontWeight: 700 } }}>
                  <TableCell colSpan={6}>TOTAL</TableCell>
                  <TableCell align="right">${totalAmount.toFixed(2)}</TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default CashDepositsPage;
